import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { MdSecurity, MdEmail, MdCheck, MdWarning, MdPerson } from 'react-icons/md';

function AdminProfilePage() {
  const { user } = useAuth();
  const [recoveryEmail, setRecoveryEmail] = useState({ email: '', hasEmail: false, masked: '' });
  const [newRecoveryEmail, setNewRecoveryEmail] = useState('');
  const [showRecoveryForm, setShowRecoveryForm] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailMessage, setEmailMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const loadRecoveryEmail = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/recovery-email`);
        setRecoveryEmail({
          email: res.data.recovery_email || '',
          hasEmail: res.data.has_recovery_email || false,
          masked: res.data.recovery_email || ''
        });
      } catch {
        console.log('Recovery email not configured');
      }
    };
    loadRecoveryEmail();
  }, []);

  const handleSaveRecoveryEmail = async (e) => {
    e.preventDefault();
    if (!newRecoveryEmail || !newRecoveryEmail.includes('@')) {
      setEmailMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }
    setSavingEmail(true);
    setEmailMessage({ type: '', text: '' });
    try {
      const response = await axios.post(`${API_URL}/api/admin/recovery-email`, {
        recovery_email: newRecoveryEmail
      });
      setRecoveryEmail({
        email: newRecoveryEmail,
        hasEmail: true,
        masked: response.data.recovery_email
      });
      setShowRecoveryForm(false);
      setNewRecoveryEmail('');
      setEmailMessage({ type: 'success', text: 'Recovery email saved successfully!' });
      setTimeout(() => setEmailMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setEmailMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to save recovery email' });
    } finally {
      setSavingEmail(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center">
          <MdPerson className="text-3xl text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800 font-rubik">{user?.username || 'Admin'}</h1>
          <p className="text-gray-500 font-rubik text-sm">Administrator Account</p>
        </div>
      </div>

      {/* Security Section */}
      <div className="card-container border border-gray-200" data-testid="admin-security-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center">
            <MdSecurity className="text-xl text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800 font-rubik">Account Security</h2>
            <p className="text-sm text-gray-500">Manage your admin account security settings</p>
          </div>
        </div>

        {emailMessage.text && (
          <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
            emailMessage.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {emailMessage.type === 'success' ? <MdCheck /> : <MdWarning />}
            {emailMessage.text}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* 2FA Status */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MdSecurity className="text-green-600" />
              <span className="font-medium text-gray-800">Two-Factor Authentication</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <MdCheck className="mr-1" /> Enabled
              </span>
              <span className="text-sm text-gray-500">Required for admin accounts</span>
            </div>
          </div>

          {/* Recovery Email */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <MdEmail className="text-blue-600" />
                <span className="font-medium text-gray-800">Recovery Email</span>
              </div>
              {!showRecoveryForm && (
                <button
                  onClick={() => setShowRecoveryForm(true)}
                  className="text-sm text-primary hover:underline"
                  data-testid="change-recovery-email-btn"
                >
                  {recoveryEmail.hasEmail ? 'Change' : 'Set up'}
                </button>
              )}
            </div>

            {showRecoveryForm ? (
              <form onSubmit={handleSaveRecoveryEmail} className="space-y-2">
                <input
                  type="email"
                  value={newRecoveryEmail}
                  onChange={(e) => setNewRecoveryEmail(e.target.value)}
                  placeholder="Enter recovery email"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  data-testid="recovery-email-input"
                />
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={savingEmail}
                    className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
                    data-testid="save-recovery-email-btn"
                  >
                    {savingEmail ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowRecoveryForm(false); setNewRecoveryEmail(''); }}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="text-sm">
                {recoveryEmail.hasEmail ? (
                  <span className="text-gray-600">{recoveryEmail.masked}</span>
                ) : (
                  <span className="text-yellow-600 flex items-center gap-1">
                    <MdWarning /> Not configured
                  </span>
                )}
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">
              Used for password recovery if you forget your password
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminProfilePage;
